import { ArrowRight } from "lucide-react"
import { useEffect } from "react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { VANCLEEF_COPY } from "@/content/marketing-vancleef"
import { captureAnalyticsEvent } from "@/lib/analytics"
import { localizedHomeUrl } from "@/lib/marketing-structured-data"
import type { MarketingPageProps } from "@/types/marketing"
import loginImage from "../../assets/marketing/clients/vancleef/login.webp"
import logoImage from "../../assets/marketing/clients/vancleef/logo.webp"

const metricLabels = {
  hr: { measured: "Izmjereno", capacity: "Kapacitet sustava", estimate: "Procjena · nije produkcijska analitika" },
  en: { measured: "Measured", capacity: "System capacity", estimate: "Estimate · not production analytics" },
} as const

export default function MarketingVanCleef(props: MarketingPageProps) {
  const copy = VANCLEEF_COPY[props.locale]
  useEffect(() => {
    captureAnalyticsEvent("case_study_viewed", { locale: props.locale, page_id: props.page_key })
  }, [props.locale, props.page_key])
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CreativeWork",
        name: props.title,
        description: props.description,
        url: props.canonical_url,
        inLanguage: props.locale === "hr" ? "hr-HR" : "en",
        creator: { "@type": "Organization", name: "Zmile d.o.o.", url: localizedHomeUrl(props.locale) },
        about: { "@type": "Organization", name: "VanCleef" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: props.locale === "hr" ? "Početna" : "Home", item: localizedHomeUrl(props.locale) },
          { "@type": "ListItem", position: 2, name: props.locale === "hr" ? "Radovi" : "Work", item: new URL(props.work_href, localizedHomeUrl(props.locale)).toString() },
          { "@type": "ListItem", position: 3, name: "VanCleef", item: props.canonical_url },
        ],
      },
    ],
  }

  return (
    <>
      <MarketingMetadata
        alternateUrls={props.alternate_urls}
        canonicalUrl={props.canonical_url}
        description={props.description}
        locale={props.locale}
        noindex={props.noindex}
        socialImageUrl={props.social_image_url}
        structuredData={structuredData}
        title={props.title}
      />
      <MarketingLayout
        consultationHref={props.consultation_href}
        currentPageKey={props.page_key}
        footer={props.footer}
        homeHref={props.home_href}
        languageSwitch={props.language_switch}
        locale={props.locale}
        navigation={props.navigation}
      >
        <nav className="case-breadcrumbs" aria-label={props.locale === "hr" ? "Putanja stranice" : "Breadcrumb"}>
          <Link className="type-marketing-caption type-marketing-muted" href={props.home_href}>{props.locale === "hr" ? "Početna" : "Home"}</Link>
          <span className="type-marketing-caption type-marketing-muted" aria-hidden="true">/</span>
          <Link className="type-marketing-caption type-marketing-muted" href={props.work_href}>{props.locale === "hr" ? "Radovi" : "Work"}</Link>
          <span className="type-marketing-caption type-marketing-muted" aria-hidden="true">/</span>
          <span className="type-marketing-caption type-marketing-muted" aria-current="page">VanCleef</span>
        </nav>
        <article className="case-page case-vancleef">
          <header className="case-hero">
            <div className="case-hero-copy">
              <h1 className="type-marketing-hero-display">{props.heading}</h1>
              <p className="case-relationship">{copy.relationship}</p>
              <p className="type-marketing-lead">{copy.context}</p>
            </div>
            <div className="case-client-mark">
              <img alt={props.locale === "hr" ? "Logotip klijenta VanCleef" : "VanCleef client logo"} height="200" src={logoImage} width="200" />
              <span className="type-marketing-caption">VanCleef</span>
            </div>
          </header>

          <section className="case-problem">
            <h2 className="type-marketing-section-display">{copy.problem.heading}</h2>
            <p className="type-marketing-prose">{copy.problem.body}</p>
          </section>

          <figure className="case-system-map" aria-label={copy.system.label}>
            <figcaption className="type-marketing-caption">{copy.system.label}</figcaption>
            <div className="case-system-core"><strong>VanCleef</strong><small>Zmile</small></div>
            <ol>
              {copy.system.nodes.map((node, index) => (
                <li key={node}><span>{String(index + 1).padStart(2, "0")}</span><strong>{node}</strong></li>
              ))}
            </ol>
          </figure>

          <section className="case-workflow">
            <header>
              <h2 className="type-marketing-section-display">{copy.workflow.heading}</h2>
            </header>
            <ol>
              {copy.workflow.items.map((item, index) => (
                <li key={item.title}>
                  <span className="type-marketing-index">{String(index + 1).padStart(2, "0")}</span>
                  <h3 className="type-marketing-list-display">{item.title}</h3>
                  <p className="type-marketing-body-small">{item.body}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="case-evidence">
            <header>
              <h2 className="type-marketing-section-display type-marketing-inverse">{copy.evidence.heading}</h2>
              <p className="type-marketing-prose type-marketing-inverse-muted">{copy.evidence.introduction}</p>
            </header>
            <ol className="case-metrics">
              {copy.evidence.metrics.map((metric) => (
                <li key={metric.value} data-evidence-kind={metric.kind}>
                  <span className="case-evidence-label">{metricLabels[props.locale][metric.kind]}</span>
                  <strong>{metric.value}</strong>
                  <p>{metric.label}</p>
                  {metric.basis && <small>{metric.basis}</small>}
                </li>
              ))}
            </ol>
            <div className="case-implementation">
              <p className="type-marketing-caption type-marketing-inverse-muted">{copy.evidence.implementationLabel}</p>
              <ul>{copy.evidence.implementation.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          </section>

          <figure className="case-production-media">
            <div>
              <p className="type-marketing-eyebrow">{copy.media.label}</p>
              <figcaption className="type-marketing-prose">{copy.media.caption}</figcaption>
            </div>
            <img alt={copy.media.alt} height="981" loading="lazy" src={loginImage} width="1710" />
          </figure>

          <section className="case-limits">
            <h2 className="type-marketing-section-display">{copy.limits.heading}</h2>
            <p className="type-marketing-prose">{copy.limits.body}</p>
          </section>

          <section className="case-final">
            <div>
              <h2 className="type-marketing-section-display">{copy.final.heading}</h2>
              <p className="type-marketing-prose">{copy.final.body}</p>
            </div>
            <Button asChild variant="marketing" size="marketingLg">
              <Link href={props.consultation_href} data-analytics-id="vancleef-consultation">{copy.final.action}<ArrowRight className="marketing-inline-icon" aria-hidden="true" /></Link>
            </Button>
          </section>
        </article>
      </MarketingLayout>
    </>
  )
}
